import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { logError } from 'commons/logger';
import type { ChatMessage } from '../types';

interface AgentChatContextType {
  messages: ChatMessage[];
  isLoading: boolean;
  addUserMessage: (content: string) => ChatMessage;
  addAiMessage: (message: ChatMessage) => void;
  addErrorMessage: (errorText: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  clearConversation: () => void;
  isConversationEmpty: boolean;
  updateMessageConfirmation: (messageId: string, confirmed: boolean) => void;
  updateMessageFeedback: (messageId: string, feedback: number | null) => void;
  sessionId: string | null;
  lastRunId: string | null;
}

const AgentChatContext = createContext<AgentChatContextType | undefined>(
  undefined,
);

interface AgentChatProviderProps {
  children: ReactNode;
}

/**
 * AgentChatProvider is a temporary in-memory storage for chat messages.
 * This serves as a placeholder until a permanent, persistent solution is implemented.
 * It manages the chat conversation state including messages and session persistence.
 */
export function AgentChatProvider({ children }: AgentChatProviderProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [lastRunId, setLastRunId] = useState<string | null>(null);

  const addUserMessage = useCallback(
    (content: string): ChatMessage => {
      const userMessage: ChatMessage = {
        id: uuidv4(),
        content: content.trim(),
        isUser: true,
        timestamp: new Date(),
        actionChain: [],
        sessionId,
      };

      setMessages((prev) => {
        const idExists = prev.some((msg) => msg.id === userMessage.id);
        if (idExists) {
          logError('Duplicate message ID detected', new Error(), {
            messageId: userMessage.id,
            messageType: 'user',
          });
          return prev;
        }
        const updatedMessages = [...prev, userMessage];
        return updatedMessages.slice(-15);
      });
      return userMessage;
    },
    [sessionId],
  );

  const addAiMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => {
      const idExists = prev.some(
        (msg) => msg.id && message.id && msg.id === message.id,
      );
      if (idExists) {
        logError('Duplicate message ID detected', new Error(), {
          messageId: message.id,
          messageType: 'ai',
        });
        return prev;
      }
      const updatedMessages = [...prev, message];
      return updatedMessages.slice(-15);
    });
    if (message.sessionId !== undefined) {
      setSessionId(message.sessionId ?? null);
      setLastRunId(message.id);
    }
  }, []);

  const addErrorMessage = useCallback(
    (errorText: string) =>
      addAiMessage({
        id: uuidv4(),
        content: errorText,
        isUser: false,
        timestamp: new Date(),
        actionChain: [],
      }),
    [addAiMessage],
  );

  const clearConversation = useCallback(() => {
    setMessages([]);
    setIsLoading(false);
    setSessionId(null);
    setLastRunId(null);
  }, []);

  const updateMessageConfirmation = useCallback(
    (messageId: string, confirmed: boolean) => {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId ? { ...message, confirmed } : message,
        ),
      );
    },
    [],
  );

  const updateMessageFeedback = useCallback(
    (messageId: string, feedback: number | null) => {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId ? { ...message, feedback } : message,
        ),
      );
    },
    [],
  );

  const isConversationEmpty = messages.length === 0;

  return (
    <AgentChatContext.Provider
      value={{
        messages,
        isLoading,
        addUserMessage,
        addAiMessage,
        addErrorMessage,
        setIsLoading,
        clearConversation,
        isConversationEmpty,
        updateMessageConfirmation,
        updateMessageFeedback,
        sessionId,
        lastRunId,
      }}
    >
      {children}
    </AgentChatContext.Provider>
  );
}

export function useAgentChatContext() {
  const context = useContext(AgentChatContext);
  if (context === undefined) {
    throw new Error(
      'useAgentChatContext must be used within a AgentChatProvider',
    );
  }
  return context;
}
